import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ShoppingCart, ExternalLink } from "lucide-react";
import { Pill } from "@/components/perf";
import ProductGallery from "@/components/ProductGallery";
import VariantPicker, { variantComplete, productImages, colorPhotoIndex, variantPrice } from "@/components/VariantPicker";

export default function ProductDetailModal({ product: p, startIndex = 0, variant, onVariant, onAdd, onCoin, onClose }) {
  const [v, setV] = useState(variant || {});
  const [jump, setJump] = useState(startIndex);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  if (!p) return null;
  const pick = (nv) => { setV(nv); onVariant?.(nv); const ci = colorPhotoIndex(p, nv); if (ci != null) setJump(ci); };
  const ready = variantComplete(p, v);
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="product-detail-modal">
      <div className="bg-pb-surface border border-pb-border w-full max-w-5xl max-h-[92vh] overflow-y-auto grid md:grid-cols-[3fr_2fr] animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <ProductGallery images={productImages(p)} alt={p.name} fit="contain" jumpTo={jump}
          className="aspect-square md:aspect-auto md:min-h-[520px] bg-black" grayscale={p.sold_out} testid="detail-gallery" />
        <div className="p-5 sm:p-7 flex flex-col">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Pill color="#4ADE80">{p.category}</Pill>
              {p.company && <span className="text-[10px] uppercase tracking-widest text-zinc-500">{p.company}</span>}
              <span className="text-[10px] uppercase tracking-widest text-zinc-600">{p.level}</span>
            </div>
            <button onClick={onClose} data-testid="product-detail-close" className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <h2 className="font-display font-black uppercase text-2xl sm:text-3xl leading-none mb-3" data-testid="product-detail-name">{p.name}</h2>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="font-display font-black text-3xl text-pb-red" data-testid="product-detail-price">{p.has_options && !v.option ? <span className="text-xs text-zinc-500 font-sans font-normal uppercase tracking-widest mr-2">from</span> : null}${variantPrice(p, v)}</span>
            {p.on_sale && <span className="text-sm text-zinc-500 line-through">${p.original_price}</span>}
            {p.sold_out ? <Pill color="#EF4444">Sold Out</Pill> : p.low ? <span className="text-[10px] uppercase tracking-widest text-amber-300">only {p.stock_quantity} left</span> : null}
          </div>
          <div className="text-sm text-zinc-300 whitespace-pre-line leading-relaxed mb-5" data-testid="product-detail-desc">{p.desc || "No description yet."}</div>
          <div className="mb-5"><VariantPicker product={p} value={v} onChange={pick} size="lg" testid="detail-variant" /></div>
          <div className="mt-auto space-y-2">
            {p.ambassador_link ? (
              <a href={p.ambassador_link} target="_blank" rel="noopener noreferrer" data-testid="product-detail-buynow"
                className="w-full flex items-center justify-center gap-2 bg-pb-red text-black py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#BEF264] transition-colors">
                Buy Now <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <button data-testid="product-detail-add" disabled={p.sold_out || !ready} onClick={() => onAdd(v)}
                className="w-full flex items-center justify-center gap-2 bg-pb-red text-black py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <ShoppingCart className="w-4 h-4" /> {p.sold_out ? "Sold Out" : ready ? "Add to Cart" : "Choose options above"}
              </button>
            )}
            {p.price_coins && !p.ambassador_link && (
              <button data-testid="product-detail-coin" disabled={p.sold_out || !ready} onClick={() => onCoin(v)}
                className="w-full border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest py-2.5 text-xs transition-colors disabled:opacity-40">
                🪙 Buy with Coin · {p.price_coins} BLC
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
